import { useEffect, useState } from "react";

import type { CSSProperties } from "react";

/** Browser (non-WebView) keyboard avoidance */
export function useBrowserKeyboardViewport(enabled: boolean): CSSProperties | null {
  const [rect, setRect] = useState<{ top: number; height: number } | null>(null);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!enabled || !vv) {
      setRect(null);
      return undefined;
    }
    // Tallest viewport seen = the keyboard-closed height.
    let fullHeight = vv.height;
    const apply = () => {
      if (vv.height > fullHeight) fullHeight = vv.height;
      const keyboardOpen = vv.height < fullHeight - 100;
      setRect(keyboardOpen ? { top: vv.offsetTop, height: vv.height } : null);
    };

    // visualViewport fires on every frame of the keyboard open/close animation.
    // Re-rendering the message list each frame is what looks laggy, so debounce:
    // leave the surface put during the animation and resize once it settles.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const update = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(apply, 120);
    };

    apply();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      if (timer) clearTimeout(timer);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [enabled]);

  if (!rect) return null;
  return {
    position: "fixed",
    top: rect.top,
    left: 0,
    right: 0,
    width: "100%",
    height: rect.height,
    bottom: "auto",
  };
}

/**
 * Browser keyboard avoidance for bottom-pinned UI. Returns how many px of the
 * layout viewport's bottom the on-screen keyboard covers, or 0 when it's closed.
 * iOS Safari only shrinks the visual viewport for the keyboard, so anything
 * pinned to the layout viewport's bottom ends up behind it.
 */
export function useBrowserKeyboardInset(enabled: boolean): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!enabled || !vv) {
      setInset(0);
      return undefined;
    }
    const update = () => {
      const covered = window.innerHeight - (vv.offsetTop + vv.height);
      setInset(Math.max(0, Math.round(covered)));
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return inset;
}

/**
 * WebView keyboard avoidance. Returns the keyboard height (px)
 * the message list should add as extra top padding so its top content clears the
 * native lift, or 0 when the keyboard is closed / outside the WebView (the bridge
 * event only fires from native). The message list applies it itself, so it takes
 * effect the moment the list renders — even if that's after the keyboard opened.
 */
export function useWebViewKeyboardInset(): number {
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const onKeyboard = (event: Event) => {
      const { detail } = event as CustomEvent<{ open?: boolean; height?: number }>;
      setHeight(detail?.open ? Math.round(detail.height ?? 0) : 0);
    };
    window.addEventListener("corca:chatKeyboard", onKeyboard);
    return () => window.removeEventListener("corca:chatKeyboard", onKeyboard);
  }, []);

  return height;
}
